import { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { toast } from 'react-toastify';
import { ReportPhotoDTO, PhotoContextAdapter, BatchPhotoUpdateDTO, PhotoUploadResponse } from '../types';
import { rotateBase64ImageByQuarterTurns } from '../utils/imageProcessing.util';

const sortPhotos = <TPhoto extends ReportPhotoDTO>(photos: TPhoto[]): TPhoto[] => {
  if (photos.length === 0) return photos;
  return [...photos].sort((a, b) => a.sortOrder - b.sortOrder);
};

interface LocalPhotoState<TPhoto extends ReportPhotoDTO = ReportPhotoDTO> {
  photo: TPhoto;
  caption: string;
  originalCaption: string;
  hasCaptionChanged: boolean;
  pendingRotation: number; // cumulative 90° clockwise turns pending save
}

interface UsePhotoStateProps<TPhoto extends ReportPhotoDTO = ReportPhotoDTO> {
  reportId: string;
  photoContext: PhotoContextAdapter<TPhoto>;
  batchUpdatePhotos: (reportId: string, updates: BatchPhotoUpdateDTO) => Promise<void>;
  deletePhoto: (reportId: string, photoId: string) => Promise<void>;
  getPhotoUrl: (blobName: string) => Promise<string>;
  getImageAsBase64?: (blobName: string) => Promise<string>;
  updateEditedPhoto?: (reportId: string, photoId: string, blob: Blob) => Promise<TPhoto>;
  onPhotosChange?: (photos: TPhoto[]) => void;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
}

const AUTO_SORT_STORAGE_PREFIX = 'photos-auto-sorted:';

export const usePhotoState = <TPhoto extends ReportPhotoDTO = ReportPhotoDTO>({
  reportId,
  photoContext,
  batchUpdatePhotos,
  deletePhoto,
  getPhotoUrl,
  getImageAsBase64,
  updateEditedPhoto,
  onPhotosChange,
  onUnsavedChangesChange,
}: UsePhotoStateProps<TPhoto>) => {
  const { photos, photoUrls, photosLoaded, setPhotos, setPhotoUrls, loadPhotos } = photoContext;

  const [originalPhotoOrder, setOriginalPhotoOrder] = useState<string[]>([]);
  const [localPhotoStates, setLocalPhotoStates] = useState<Record<string, LocalPhotoState>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savingRotationIds, setSavingRotationIds] = useState<Set<string>>(new Set());
  const hasInitializedRef = useRef(false);
  const lastReportIdRef = useRef<string>(reportId);
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasUserReorderedRef = useRef(false);

  const clearAutoSaveTimeout = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
  }, []);

  // Reset initialization when reportId changes
  useEffect(() => {
    if (lastReportIdRef.current !== reportId) {
      hasInitializedRef.current = false;
      lastReportIdRef.current = reportId;
      setLocalPhotoStates({});
      setOriginalPhotoOrder([]);
      setHasUnsavedChanges(false);
    }
  }, [reportId]);

  // Initialize local photo states when photos are first loaded from context.
  // Apply smart sort on load: currently always by sortOrder.
  useEffect(() => {
    if (photosLoaded && photos && photos.length > 0 && !hasInitializedRef.current) {
      const sorted = sortPhotos(photos as ReportPhotoDTO[]) as TPhoto[];
      const sortedIds = sorted.map(p => p.id);
      setOriginalPhotoOrder(sortedIds);

      // If sort produced a different order than what came from the backend, push sorted list
      // into context so the UI renders in the correct order immediately.
      const backendIds = photos.map(p => p.id);
      if (JSON.stringify(backendIds) !== JSON.stringify(sortedIds)) {
        setPhotos(sorted);
        // Persist the corrected order so the backend is kept consistent
        (async () => {
          try {
            await batchUpdatePhotos(reportId, { updates: [], captionUpdates: [], reorder: sortedIds });
          } catch (error) {
            console.error('[Photos] Failed to persist corrected sort order on load', error);
            toast.error('Failed to save photo order');
          }
        })();
      }

      const initialStates: Record<string, LocalPhotoState> = {};
      sorted.forEach(photo => {
        initialStates[photo.id] = {
          photo,
          caption: photo.caption || '',
          originalCaption: photo.caption || '',
          hasCaptionChanged: false,
          pendingRotation: 0,
        };
      });
      setLocalPhotoStates(initialStates);
      hasInitializedRef.current = true;

      onPhotosChange?.(sorted);
    }
  }, [photosLoaded, photos, onPhotosChange, reportId, setPhotos, batchUpdatePhotos]);

  // Sync new photos to localPhotoStates after initialization, preserving existing unsaved changes
  // This effect only adds new photos - it does NOT modify existing states to preserve unsaved caption changes
  useEffect(() => {
    if (hasInitializedRef.current && photos) {
      setLocalPhotoStates(prev => {
        const newStates = { ...prev };
        let hasChanges = false;

        // Add states for any new photos that don't exist yet
        photos.forEach(photo => {
          if (!newStates[photo.id]) {
            newStates[photo.id] = {
              photo,
              caption: photo.caption || '',
              originalCaption: photo.caption || '',
              hasCaptionChanged: false,
              pendingRotation: 0,
            };
            hasChanges = true;
          }
          // DO NOT update existing states - preserve unsaved caption changes
        });

        // Clean up states for photos that no longer exist (shouldn't happen often since handleDeletePhoto handles this)
        const currentPhotoIds = new Set(photos.map(p => p.id));
        Object.keys(newStates).forEach(photoId => {
          if (!currentPhotoIds.has(photoId)) {
            delete newStates[photoId];
            hasChanges = true;
          }
        });

        return hasChanges ? newStates : prev;
      });
    }
  }, [photos]);

  const handleCaptionChange = useCallback(
    (photoId: string, caption: string) => {
      setLocalPhotoStates(prev => {
        const currentState = prev[photoId];
        if (!currentState) return prev;

        const hasChanged = caption !== currentState.originalCaption;
        const newState = {
          ...currentState,
          caption,
          hasCaptionChanged: hasChanged,
        };

        const newStates = { ...prev, [photoId]: newState };

        // Check if any photos have unsaved changes (caption or order)
        const hasCaptionChanges = Object.values(newStates).some(state => state.hasCaptionChanged);
        const hasOrderChanges = JSON.stringify(photos.map(p => p.id)) !== JSON.stringify(originalPhotoOrder);
        const hasAnyChanges = hasCaptionChanges || hasOrderChanges;

        setHasUnsavedChanges(hasAnyChanges);
        onUnsavedChangesChange?.(hasAnyChanges);

        // Create updated photos array with new caption for completion status check
        const updatedPhotos = photos.map(photo => {
          if (photo.id === photoId) {
            return {
              ...photo,
              caption: caption,
              hasCaption: caption.trim().length > 0,
            };
          }
          return photo;
        });

        // Trigger completion status update with updated photo data
        onPhotosChange?.(updatedPhotos);

        return newStates;
      });
    },
    [photos, originalPhotoOrder, onUnsavedChangesChange, onPhotosChange],
  );

  const handleUpdateCaption = useCallback(
    (photoId: string, caption: string) => {
      // This is for immediate updates (not used in the new flow)
      handleCaptionChange(photoId, caption);
    },
    [handleCaptionChange],
  );

  const handleDeletePhoto = useCallback(
    async (photoId: string) => {
      try {
        await deletePhoto(reportId, photoId);

        // Remove from local state
        setPhotos(prev => {
          const newPhotos = prev.filter(photo => photo.id !== photoId);
          // Trigger completion status update
          onPhotosChange?.(newPhotos);
          return newPhotos;
        });
        setOriginalPhotoOrder(prev => prev.filter(id => id !== photoId));
        setLocalPhotoStates(prev => {
          const newStates = { ...prev };
          delete newStates[photoId];
          return newStates;
        });
        setPhotoUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[photoId];
          return newUrls;
        });

        toast.success('Photo deleted successfully');
      } catch (error) {
        console.error('Error deleting photo:', error);
        toast.error('Failed to delete photo');
      }
    },
    [reportId, onPhotosChange, deletePhoto],
  );

  const handleRotatePhoto = useCallback(
    (photoId: string) => {
      setLocalPhotoStates(prev => {
        const currentState = prev[photoId];
        if (!currentState) return prev;
        return {
          ...prev,
          [photoId]: { ...currentState, pendingRotation: (currentState.pendingRotation + 1) % 4 },
        };
      });
      // Rotation puts the step into draft state so changes aren't silently lost on navigation
      setHasUnsavedChanges(true);
      onUnsavedChangesChange?.(true);
    },
    [onUnsavedChangesChange],
  );

  const handleSaveChanges = useCallback(async () => {
    setIsSaving(true);
    let hasRotationFailure = false;

    // Save all pending rotations in parallel (covers the "Next" navigation path)
    if (getImageAsBase64 && updateEditedPhoto) {
      const photosWithRotation = Object.values(localPhotoStates).filter(s => s.pendingRotation > 0 && !savingRotationIds.has(s.photo.id));

      if (photosWithRotation.length > 0) {
        // Mark all as saving up front so the per-photo spinners show
        setSavingRotationIds(prev => {
          const next = new Set(prev);
          photosWithRotation.forEach(s => next.add(s.photo.id));
          return next;
        });

        const results = await Promise.allSettled(
          photosWithRotation.map(async state => {
            const savedRotations = state.pendingRotation;
            const sourceBlobName = state.photo.blobName || state.photo.originalBlobName;
            const base64 = await getImageAsBase64(sourceBlobName);
            const finalBlob = await rotateBase64ImageByQuarterTurns(base64, savedRotations);
            const updatedPhoto = await updateEditedPhoto(reportId, state.photo.id, finalBlob);
            const refreshedPhoto = {
              ...updatedPhoto,
              updatedOn: updatedPhoto.updatedOn || new Date().toISOString(),
            } as TPhoto;
            const newThumbnailUrl = await getPhotoUrl(refreshedPhoto.thumbnailBlobName || refreshedPhoto.blobName);
            return { state, savedRotations, refreshedPhoto, newThumbnailUrl };
          }),
        );

        results.forEach((result, i) => {
          const photoId = photosWithRotation[i].photo.id;
          if (result.status === 'fulfilled') {
            const { savedRotations, refreshedPhoto, newThumbnailUrl } = result.value;
            setLocalPhotoStates(prev => {
              if (!prev[photoId]) return prev;
              const remaining = (prev[photoId].pendingRotation - savedRotations + 4) % 4;
              return { ...prev, [photoId]: { ...prev[photoId], photo: refreshedPhoto, pendingRotation: remaining } };
            });
            setPhotos(prev => prev.map(p => (p.id === refreshedPhoto.id ? refreshedPhoto : p)));
            setPhotoUrls(prev => ({ ...prev, [refreshedPhoto.id]: newThumbnailUrl }));
          } else {
            console.error(`Error saving rotation for photo ${photoId}:`, result.reason);
            toast.error('Failed to save photo rotation');
            hasRotationFailure = true;
          }
        });

        setSavingRotationIds(prev => {
          const next = new Set(prev);
          photosWithRotation.forEach(s => next.delete(s.photo.id));
          return next;
        });
      }
    }

    // Get all photos with changed captions
    const photosToUpdate = Object.values(localPhotoStates)
      .filter(state => state.hasCaptionChanged)
      .map(state => ({
        photoId: state.photo.id,
        caption: state.caption,
      }));

    // Check if order has changed
    const hasOrderChanged = JSON.stringify(photos.map(p => p.id)) !== JSON.stringify(originalPhotoOrder);

    if (photosToUpdate.length > 0 || hasOrderChanged) {
      // Batch update captions and/or reorder
      await batchUpdatePhotos(reportId, {
        // Include both shapes to satisfy different backend DTOs
        updates: photosToUpdate.map(update => ({ id: update.photoId, caption: update.caption })),
        captionUpdates: photosToUpdate.map(update => ({ photoId: update.photoId, caption: update.caption })),
        reorder: photos.map(photo => photo.id), // Current order
      });

      // Update the context photos with new captions
      flushSync(() => {
        setPhotos(prev => {
          return prev.map(photo => {
            const update = photosToUpdate.find(u => u.photoId === photo.id);
            if (update) {
              return {
                ...photo,
                caption: update.caption,
                hasCaption: update.caption.trim().length > 0,
              };
            }
            return photo;
          });
        });
      });

      // Update local states to mark as saved
      setLocalPhotoStates(prev => {
        const newStates = { ...prev };
        photosToUpdate.forEach(({ photoId, caption }) => {
          if (newStates[photoId]) {
            newStates[photoId] = {
              ...newStates[photoId],
              caption,
              originalCaption: caption,
              hasCaptionChanged: false,
            };
          }
        });
        return newStates;
      });

      // Update original order
      setOriginalPhotoOrder(photos.map(photo => photo.id));
    }

    if (!hasRotationFailure) {
      setHasUnsavedChanges(false);
      onUnsavedChangesChange?.(false);
    }
    setIsSaving(false);
  }, [
    reportId,
    localPhotoStates,
    savingRotationIds,
    photos,
    originalPhotoOrder,
    setPhotos,
    setPhotoUrls,
    onUnsavedChangesChange,
    batchUpdatePhotos,
    getImageAsBase64,
    updateEditedPhoto,
    getPhotoUrl,
  ]);

  const handleSaveRotation = useCallback(
    async (photoId: string) => {
      if (!getImageAsBase64 || !updateEditedPhoto) return;
      const state = localPhotoStates[photoId];
      if (!state || state.pendingRotation === 0) return;

      setSavingRotationIds(prev => new Set(prev).add(photoId));
      const savedRotations = state.pendingRotation;
      try {
        const sourceBlobName = state.photo.blobName || state.photo.originalBlobName;
        const base64 = await getImageAsBase64(sourceBlobName);
        const finalBlob = await rotateBase64ImageByQuarterTurns(base64, savedRotations);
        const updatedPhoto = await updateEditedPhoto(reportId, photoId, finalBlob);
        // Force a fresh cache-busting key so the rotated thumbnail is re-fetched.
        const refreshedPhoto = {
          ...updatedPhoto,
          updatedOn: updatedPhoto.updatedOn || new Date().toISOString(),
        } as TPhoto;
        const newThumbnailUrl = await getPhotoUrl(refreshedPhoto.thumbnailBlobName || refreshedPhoto.blobName);
        // Subtract only the rotations we actually saved so any clicks that arrived
        // during the in-flight save are preserved, not dropped.
        setLocalPhotoStates(prev => {
          if (!prev[photoId]) return prev;
          const currentPending = prev[photoId].pendingRotation;
          const remaining = (currentPending - savedRotations + 4) % 4;
          const updated = {
            ...prev,
            [photoId]: { ...prev[photoId], photo: refreshedPhoto, pendingRotation: remaining },
          };
          // Clear draft state only if no other changes remain (rotation, caption, or order)
          const hasRotationOrCaptionChanges =
            Object.values(updated).some(s => s.pendingRotation > 0) || Object.values(updated).some(s => s.hasCaptionChanged);
          const hasOrderChanges = JSON.stringify(photos.map(p => p.id)) !== JSON.stringify(originalPhotoOrder);
          if (!hasRotationOrCaptionChanges && !hasOrderChanges) {
            setHasUnsavedChanges(false);
            onUnsavedChangesChange?.(false);
          }
          return updated;
        });
        setPhotos(prev => prev.map(p => (p.id === refreshedPhoto.id ? refreshedPhoto : p)));
        setPhotoUrls(prev => ({ ...prev, [refreshedPhoto.id]: newThumbnailUrl }));
      } catch (error) {
        console.error(`Error saving rotation for photo ${photoId}:`, error);
        toast.error('Failed to save photo rotation');
      } finally {
        setSavingRotationIds(prev => {
          const next = new Set(prev);
          next.delete(photoId);
          return next;
        });
      }
    },
    [
      localPhotoStates,
      photos,
      originalPhotoOrder,
      reportId,
      getImageAsBase64,
      updateEditedPhoto,
      getPhotoUrl,
      setPhotos,
      setPhotoUrls,
      onUnsavedChangesChange,
    ],
  );

  useEffect(() => {
    const hasCaptionChanges = Object.values(localPhotoStates).some(state => state.hasCaptionChanged);
    if (!hasCaptionChanges) {
      clearAutoSaveTimeout();
      return;
    }
    if (isSaving) {
      return;
    }
    clearAutoSaveTimeout();
    autoSaveTimeoutRef.current = setTimeout(() => {
      void handleSaveChanges();
    }, 300);
    return () => {
      clearAutoSaveTimeout();
    };
  }, [localPhotoStates, isSaving, handleSaveChanges, clearAutoSaveTimeout]);

  const handleCancelChanges = useCallback(() => {
    // Reset local states to original values
    setLocalPhotoStates(prev => {
      const newStates = { ...prev };
      Object.keys(newStates).forEach(photoId => {
        newStates[photoId] = {
          ...newStates[photoId],
          caption: newStates[photoId].originalCaption,
          hasCaptionChanged: false,
          pendingRotation: 0,
        };
      });
      return newStates;
    });

    // Reset photo order to original
    setPhotos(prev => {
      const originalPhotos = originalPhotoOrder.map(id => prev.find(photo => photo.id === id)).filter(Boolean) as TPhoto[];
      return originalPhotos;
    });

    setHasUnsavedChanges(false);
    onUnsavedChangesChange?.(false);
  }, [originalPhotoOrder, onUnsavedChangesChange]);

  const movePhoto = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      hasUserReorderedRef.current = true;
      setPhotos(prev => {
        const newPhotos = [...prev];
        const draggedPhoto = newPhotos[dragIndex];
        newPhotos.splice(dragIndex, 1);
        newPhotos.splice(hoverIndex, 0, draggedPhoto);
        // Keep sortOrder in sync with array position so that when the Photos
        // step remounts and the initialization effect re-sorts by sortOrder,
        // it reproduces the drag-and-drop order instead of reverting to the
        // stale backend values.
        return newPhotos.map((photo, idx) => ({ ...photo, sortOrder: idx }));
      });

      // Check if any photos have unsaved changes (caption or order)
      const hasCaptionChanges = Object.values(localPhotoStates).some(state => state.hasCaptionChanged);
      const hasOrderChanges = true; // We just reordered, so this is true
      const hasAnyChanges = hasCaptionChanges || hasOrderChanges;

      setHasUnsavedChanges(hasAnyChanges);
      onUnsavedChangesChange?.(hasAnyChanges);
    },
    [localPhotoStates, onUnsavedChangesChange],
  );

  // Convert upload response to full photo DTO
  const convertUploadResponseToPhoto = useCallback(
    (uploadResponse: PhotoUploadResponse): TPhoto => {
      return {
        id: uploadResponse.id,
        reportId: reportId, // Generic field name for all report types
        blobName: uploadResponse.blobName,
        originalBlobName: uploadResponse.originalBlobName,
        thumbnailBlobName: uploadResponse.thumbnailBlobName,
        fileName: uploadResponse.fileName,
        width: uploadResponse.width,
        height: uploadResponse.height,
        orientation: '1', // Default orientation
        caption: uploadResponse.caption || '',
        hasCaption: uploadResponse.hasCaption,
        sortOrder: uploadResponse.sortOrder,
        createdOn: uploadResponse.createdOn,
        updatedOn: uploadResponse.updatedOn,
      } as TPhoto;
    },
    [reportId],
  );

  // Merge uploaded photo into existing state without reloading
  const mergeUploadedPhoto = useCallback(
    async (uploadResponse: PhotoUploadResponse, options?: { insertIndex?: number }) => {
      const newPhoto = convertUploadResponseToPhoto(uploadResponse);

      // Load thumbnail URL BEFORE adding the photo to the array
      let thumbnailUrl = '';
      try {
        thumbnailUrl = await getPhotoUrl(newPhoto.thumbnailBlobName || newPhoto.blobName);
      } catch (error) {
        console.error(`Failed to load thumbnail for new photo ${newPhoto.id}:`, error);
      }

      // Use flushSync to synchronously update all photo-related state
      // This ensures the component re-renders with the new photo BEFORE
      // the uploading placeholder is removed
      flushSync(() => {
        setPhotos(prev => {
          const updatedPhotos = [...prev];
          const insertIndex = options?.insertIndex ?? updatedPhotos.length;
          updatedPhotos.splice(Math.min(insertIndex, updatedPhotos.length), 0, newPhoto);

          // Apply smart sort unless the user has manually reordered
          const finalPhotos = hasUserReorderedRef.current ? updatedPhotos : (sortPhotos(updatedPhotos as ReportPhotoDTO[]) as TPhoto[]);

          // Keep original order in sync with the current displayed order
          const sortedIds = finalPhotos.map(photo => photo.id);
          setOriginalPhotoOrder(sortedIds);

          onPhotosChange?.(finalPhotos as TPhoto[]);
          return finalPhotos as TPhoto[];
        });

        // Initialize local state for the new photo
        setLocalPhotoStates(prev => ({
          ...prev,
          [newPhoto.id]: {
            photo: newPhoto,
            caption: newPhoto.caption || '',
            originalCaption: newPhoto.caption || '',
            hasCaptionChanged: false,
            pendingRotation: 0,
          },
        }));

        // Set the thumbnail URL (now we have it)
        setPhotoUrls(prev => ({
          ...prev,
          [newPhoto.id]: thumbnailUrl,
        }));
      });

      // Debounced persist: fires 1.5s after the last upload completes.
      // This corrects the duplicate SortOrder values that arise from concurrent
      // uploads all reading the same MaxAsync(SortOrder) before any of them save.
    },
    [convertUploadResponseToPhoto, onPhotosChange, setPhotos, setPhotoUrls, setOriginalPhotoOrder, setLocalPhotoStates, getPhotoUrl],
  );

  // Merge edited photo into existing state without reloading all photos
  const mergeEditedPhoto = useCallback(
    async (updatedPhoto: TPhoto) => {
      // Load new thumbnail URL for the edited photo
      let thumbnailUrl = '';
      try {
        thumbnailUrl = await getPhotoUrl(updatedPhoto.thumbnailBlobName || updatedPhoto.blobName);
      } catch (error) {
        console.error(`Failed to load thumbnail for edited photo ${updatedPhoto.id}:`, error);
      }

      // Update the specific photo in the photos array
      setPhotos(prev => {
        const updatedPhotos = prev.map(photo => (photo.id === updatedPhoto.id ? updatedPhoto : photo));
        // Notify parent of photo change for completion status update
        onPhotosChange?.(updatedPhotos);
        return updatedPhotos;
      });

      // Update the thumbnail URL for the edited photo
      setPhotoUrls(prev => ({
        ...prev,
        [updatedPhoto.id]: thumbnailUrl,
      }));

      // Update local state if it exists (preserve caption changes)
      setLocalPhotoStates(prev => {
        if (prev[updatedPhoto.id]) {
          return {
            ...prev,
            [updatedPhoto.id]: {
              ...prev[updatedPhoto.id],
              photo: updatedPhoto,
            },
          };
        }
        return prev;
      });
    },
    [onPhotosChange, setPhotos, setPhotoUrls, setLocalPhotoStates, getPhotoUrl],
  );

  // Final sort pass after a full upload batch completes.
  // Skipped if the user has manually reordered photos.
  const sortPhotosNow = useCallback(() => {
    if (hasUserReorderedRef.current) return;
    setPhotos(prev => {
      const sorted = sortPhotos(prev as ReportPhotoDTO[]) as TPhoto[];
      const sortedIds = sorted.map(p => p.id);
      setOriginalPhotoOrder(sortedIds);
      onPhotosChange?.(sorted);
      return sorted;
    });
  }, [onPhotosChange, setPhotos]);

  return {
    photos,
    localPhotoStates,
    photoUrls,
    isLoading: !photosLoaded, // Show loading until photos are loaded
    isSaving,
    hasUnsavedChanges,
    savingRotationIds,
    handleCaptionChange,
    handleUpdateCaption,
    handleDeletePhoto,
    handleSaveChanges,
    handleCancelChanges,
    handleRotatePhoto,
    handleSaveRotation,
    movePhoto,
    loadPhotos, // Still expose loadPhotos for manual refresh if needed
    mergeUploadedPhoto,
    mergeEditedPhoto,
    sortPhotosNow,
  };
};
